import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Dialog,
  Flex,
  Text,
  Button,
  Separator,
  Callout,
} from '@radix-ui/themes';
import { ExternalLinkIcon, CrossCircledIcon } from '@radix-ui/react-icons';
import { toast } from '../../utils/toast';

interface SlackInstallationWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export default function SlackInstallationWizard({
  open,
  onOpenChange,
  onSuccess,
}: SlackInstallationWizardProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [oauthLoading, setOauthLoading] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);

  // Handle OAuth callback results from URL params
  useEffect(() => {
    const slackInstalled = searchParams.get('slack_installed');
    const slackError = searchParams.get('slack_error');

    if (slackInstalled === 'true') {
      toast.success('Slack workspace connected successfully!');
      // Clean up URL params
      searchParams.delete('slack_installed');
      searchParams.delete('installation_id');
      setSearchParams(searchParams);
      onSuccess?.();
    }

    if (slackError) {
      toast.error(`Slack installation failed: ${slackError}`);
      setOauthError(slackError);
      // Clean up URL params
      searchParams.delete('slack_error');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, onSuccess]);

  const handleInstallToSlack = async () => {
    setOauthLoading(true);
    setOauthError(null);

    try {
      const response = await fetch('/oauth/slack/authorize', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to start Slack authorization');
      }

      const data = await response.json();

      if (data.authorization_url) {
        // Redirect to Slack OAuth
        window.location.href = data.authorization_url;
      } else {
        throw new Error('No authorization URL received');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to start Slack authorization';
      setOauthError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setOauthLoading(false);
    }
  };

  const handleClose = () => {
    setOauthError(null);
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleClose}>
      <Dialog.Content style={{ maxWidth: 500 }}>
        <Flex direction="column" gap="4">
          <Dialog.Title>Connect Slack</Dialog.Title>

          <Separator size="4" />

          <Dialog.Description>
            Click the button below to install Chaintracks to your Slack workspace
          </Dialog.Description>

          <Flex direction="column" gap="3" align="center" justify="center" style={{ padding: '40px 0' }}>
            <div
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '16px',
                backgroundColor: '#4a154b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px',
              }}
            >
              <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
                <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.124 2.521a2.528 2.528 0 0 1 2.52-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.52V8.834zm-1.271 0a2.528 2.528 0 0 1-2.521 2.521 2.528 2.528 0 0 1-2.521-2.521V2.522A2.528 2.528 0 0 1 15.165 0a2.528 2.528 0 0 1 2.522 2.522v6.312zm-2.522 10.124a2.528 2.528 0 0 1 2.522 2.52A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.521-2.522v-2.52h2.521zm0-1.271a2.527 2.527 0 0 1-2.521-2.521 2.527 2.527 0 0 1 2.521-2.521h6.313A2.528 2.528 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.521h-6.313z"/>
              </svg>
            </div>

            <Text size="3" weight="medium" align="center">
              You'll be redirected to Slack to authorize Chaintracks
            </Text>

            <Text size="2" color="gray" align="center" style={{ maxWidth: '400px' }}>
              We'll request permission to read channels and send messages on your behalf.
            </Text>

            {oauthError && (
              <Callout.Root color="red" style={{ marginTop: '16px', width: '100%' }}>
                <Callout.Icon>
                  <CrossCircledIcon />
                </Callout.Icon>
                <Callout.Text>{oauthError}</Callout.Text>
              </Callout.Root>
            )}

            <Button
              size="3"
              onClick={handleInstallToSlack}
              disabled={oauthLoading}
              style={{
                marginTop: '24px',
                backgroundColor: '#4a154b',
                color: 'white',
              }}
            >
              {oauthLoading ? 'Redirecting...' : 'Add to Slack'}
              <ExternalLinkIcon />
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
